import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Send, Trash2, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const ROLE_COLORS = {
  parent: "chip-pink",
  teacher: "chip-green",
  admin: "chip-lilac",
  front_desk: "chip-amber",
};
const ROLE_LABEL = {
  parent: "Parent", teacher: "Teacher", admin: "Admin", front_desk: "Front Desk",
};

export function CommentThread({ updateId, defaultOpen = false }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(defaultOpen);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/daily-updates/${updateId}/comments`);
      setItems(data);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    if (open && items === null) load();
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [items]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/daily-updates/${updateId}/comments`, { text });
      setItems((cur) => [...(cur || []), data]);
      setText("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post");
    } finally {
      setSending(false);
    }
  };

  const del = async (cid) => {
    if (!confirm("Delete this comment?")) return;
    try {
      await api.delete(`/daily-updates/${updateId}/comments/${cid}`);
      setItems((cur) => cur.filter((c) => c.id !== cid));
    } catch {
      toast.error("Could not delete");
    }
  };

  const count = items?.length;

  return (
    <div className="mt-5 pt-4 border-t border-border">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`toggle-comments-${updateId}`}
        className="flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-foreground"
      >
        <MessageSquare size={16} />
        {count === undefined ? "Comments" : count === 0 ? "Be the first to comment" : `${count} ${count === 1 ? "comment" : "comments"}`}
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {items === null && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 size={14} className="animate-spin" /> Loading...
            </div>
          )}
          {items && items.length > 0 && (
            <div className="space-y-2">
              {items.map((c) => {
                const mine = c.user_id === user?.id;
                return (
                  <div
                    key={c.id}
                    data-testid={`comment-${c.id}`}
                    className={`rounded-2xl p-3 ${mine ? "bg-primary/8 border border-primary/20" : "bg-muted/40 border border-border"}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${ROLE_COLORS[c.user_role] || "chip-lilac"}`}>
                        {ROLE_LABEL[c.user_role] || c.user_role}
                      </span>
                      <span className="text-sm font-bold">{c.user_name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{formatTime(c.created_at)}</span>
                      {(mine || user?.role === "admin") && (
                        <button
                          onClick={() => del(c.id)}
                          className="text-muted-foreground hover:text-destructive"
                          title="Delete"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                    <div className="text-sm whitespace-pre-wrap">{c.text}</div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
          )}

          <form onSubmit={send} className="flex gap-2 items-center pt-1">
            <Input
              data-testid={`comment-input-${updateId}`}
              placeholder={user?.role === "parent" ? "Say thank you, ask a question..." : "Reply as staff..."}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="h-11 rounded-full flex-1"
              maxLength={1000}
            />
            <Button
              type="submit"
              data-testid={`comment-send-${updateId}`}
              disabled={sending || !text.trim()}
              size="icon"
              className="rounded-full h-11 w-11 flex-shrink-0"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}

function formatTime(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}
